import { type OnboardingTaskKind } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import {
  useOnboardingSystemActionTargets,
  useRunOnboardingSystemAction,
} from '@/products/onboarding/features/use-onboarding-tasks';

export function OnboardingSystemActionFields({
  taskId,
  kind,
  revision,
}: {
  taskId: string;
  kind: OnboardingTaskKind;
  revision: number;
}) {
  const [q, setQ] = useState('');
  const targets = useOnboardingSystemActionTargets(taskId, true, q.trim() || undefined);
  const run = useRunOnboardingSystemAction();
  const [assetId, setAssetId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [validTo, setValidTo] = useState('');
  const assetKind = kind === 'asset_issue' || kind === 'asset_return';
  const options = assetKind ? (targets.data?.assets ?? []) : (targets.data?.vehicles ?? []);
  const selected = assetKind ? assetId : vehicleId;
  const selectedVehicle = targets.data?.vehicles.find((item) => item.id === vehicleId);
  const poolEnd = kind === 'vehicle_assign' && selectedVehicle?.usageMode === 'pool';

  async function onRun() {
    try {
      await run.mutateAsync({
        taskId,
        body: {
          revision,
          ...(assetKind ? { assetId: selected } : { vehicleId: selected }),
          ...(poolEnd && validTo ? { validTo } : {}),
        },
      });
      toast.success('Systemaktion ausgeführt.');
    } catch (caught) {
      toast.error(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Die Systemaktion konnte nicht ausgeführt werden.',
      );
    }
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label htmlFor={`target-q-${taskId}`}>Suche</Label>
        <Input
          id={`target-q-${taskId}`}
          value={q}
          placeholder={assetKind ? 'Name oder Inventarnummer' : 'Kennzeichen oder Modell'}
          onChange={(event) => setQ(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label>{assetKind ? 'Gerät' : 'Fahrzeug'}</Label>
        <Select
          value={selected || 'none'}
          onValueChange={(value) => {
            if (value === 'none') {
              return;
            }
            if (assetKind) {
              setAssetId(value);
            } else {
              setVehicleId(value);
            }
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={targets.isPending ? 'Wird geladen …' : 'Bitte wählen'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none" disabled>
              Bitte wählen
            </SelectItem>
            {options.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {poolEnd ? (
        <div className="space-y-2">
          <Label htmlFor={`pool-end-${taskId}`}>Ende der Buchung</Label>
          <Input
            id={`pool-end-${taskId}`}
            type="date"
            value={validTo}
            onChange={(event) => setValidTo(event.target.value)}
          />
        </div>
      ) : null}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={run.isPending || selected.length !== 36 || (poolEnd && !validTo)}
        onClick={() => void onRun()}
      >
        Systemaktion ausführen
      </Button>
    </div>
  );
}
